/**
 * One capsule in a module's playlist, as a tile.
 *
 * The tile is the unit the whole feature is drawn from: the card's carousel is a
 * row of them, the module's screen shows the same row larger, and the studio's
 * slots are the same tiles with a way to move and remove them. So it is written
 * once, here, and what changes between the three places is *what pressing it
 * does* — which is a prop, because that is genuinely different in each.
 *
 * Four decisions shape it:
 *
 *  - **The number is on the tile.** A playlist is an order, and an order the
 *    reader cannot see is just a list. The position travels as a chip rather than
 *    as a caption under the tile, so a row of tiles reads as a sequence even when
 *    the titles are long enough to wrap.
 *  - **A tile with no cover is still a tile.** It paints the app's own surface and
 *    sets the title on it, the way a book's card paints a page of its own theme
 *    when it has no artwork — the alternative is a row of grey boxes that jumps as
 *    the pictures arrive, which is the one thing a carousel must never do.
 *  - **A state the creator has to fix is said on the tile** — *Draft* for a
 *    capsule written and not published, *No longer here* for one whose record
 *    cannot be read. A tile that simply looked empty would send somebody hunting
 *    for a capsule that is not missing at all.
 *  - **It is pressable, and nothing more.** No play button in the corner: what a
 *    tile does when pressed depends on where it is being looked at, and a tile
 *    with its own idea of that is how a studio ends up playing a capsule at a
 *    creator who meant to edit it.
 *
 * Width is the caller's, because a carousel is a row of *objects*: a phone shows
 * two and a half of them, a desktop six, and the objects do not change size
 * between the two.
 */
import {Platform, Pressable, StyleSheet, View} from 'react-native'
import {Image} from 'expo-image'
import {useLingui} from '@lingui/react/macro'

import {type ModuleTile} from '#/lib/moduleTiles'
import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'
import {CAPSULE_ASPECT} from '#/components/work'

/** The ink-on-glass a tile draws its chips with, over whatever the cover is. */
const CHIP_BG = 'rgba(0, 0, 0, 0.55)'
const FOOT_BG = 'rgba(0, 0, 0, 0.6)'

export function ModuleCapsuleTile({
  tile,
  width,
  onPress,
  testID,
  accessibilityHint,
  children,
}: {
  tile: ModuleTile
  /** How wide the tile is; its height follows the capsule card's proportions. */
  width: number
  onPress?: () => void
  testID?: string
  accessibilityHint?: string
  /** The studio's own furniture — a menu button, a move control. */
  children?: React.ReactNode
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const stateWord =
    tile.state === 'draft'
      ? l`Draft`
      : tile.state === 'missing'
        ? l`No longer here`
        : null

  return (
    <Pressable
      testID={testID}
      accessibilityRole={onPress ? 'button' : undefined}
      accessibilityLabel={l`Capsule ${tile.position}: ${tile.title}`}
      accessibilityHint={accessibilityHint}
      disabled={!onPress}
      onPress={onPress}
      style={[
        {width},
        Platform.OS === 'web' && onPress ? {cursor: 'pointer'} : null,
      ]}>
      {({pressed}) => (
        <View style={[pressed && onPress ? {opacity: 0.9} : null]}>
          <View
            style={[
              styles.card,
              tile.coverUri ? null : t.atoms.bg_contrast_50,
              // A place whose capsule cannot be read is drawn as the hole it is,
              // rather than with the app's ordinary surface.
              tile.state === 'missing' ? styles.missing : null,
            ]}>
            {tile.coverUri ? (
              <Image
                source={{uri: tile.coverUri}}
                style={StyleSheet.absoluteFill}
                contentFit="cover"
                accessibilityIgnoresInvertColors
              />
            ) : (
              /*
                No picture: the app's own surface with the title on it, so the row
                reads the same before and after the covers arrive.
              */
              <View
                style={[
                  StyleSheet.absoluteFill,
                  a.justify_center,
                  a.align_center,
                  a.gap_sm,
                  a.p_sm,
                ]}>
                <Text
                  emoji
                  numberOfLines={4}
                  style={[
                    a.text_xs,
                    a.font_semi_bold,
                    a.text_center,
                    t.atoms.text_contrast_medium,
                  ]}>
                  {tile.title}
                </Text>
                {stateWord ? (
                  <View style={[styles.statePill, a.rounded_full]}>
                    <Text
                      style={[a.text_xs, a.font_semi_bold, styles.chipText]}>
                      {stateWord}
                    </Text>
                  </View>
                ) : null}
              </View>
            )}

            {/* The playlist's order, said on the object it belongs to. */}
            <View style={[styles.number, styles.chip]}>
              <Text style={[a.text_xs, a.font_bold, styles.chipText]}>
                {tile.position}
              </Text>
            </View>

            {/* What the capsule pays out, in the app's own word for it. */}
            {tile.coins > 0 ? (
              <View style={[styles.coins, styles.chip]}>
                <Text style={[a.text_xs, a.font_bold, styles.chipText]}>
                  {l`${tile.coins} coins`}
                </Text>
              </View>
            ) : null}

            {/*
              On a tile that has a picture, the title rides on its foot — and the
              state takes that line's place when there is one to report, because a
              132px tile has room for one line under the number and "Draft" is the
              more useful of the two while it is true. (A tile with no picture says
              both, in the middle, where it has the room.)
            */}
            {tile.coverUri ? (
              <View style={styles.foot}>
                <Text
                  emoji
                  numberOfLines={2}
                  style={[a.text_xs, a.font_semi_bold, styles.chipText]}>
                  {stateWord ?? tile.title}
                </Text>
              </View>
            ) : null}

            {children}
          </View>
        </View>
      )}
    </Pressable>
  )
}

const styles = StyleSheet.create({
  card: {
    width: '100%',
    aspectRatio: CAPSULE_ASPECT,
    borderRadius: 10,
    overflow: 'hidden',
    justifyContent: 'flex-end',
  },
  missing: {
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(128, 128, 128, 0.5)',
    borderStyle: 'dashed',
  },
  chip: {
    position: 'absolute',
    height: 20,
    borderRadius: 10,
    paddingHorizontal: 7,
    justifyContent: 'center',
    backgroundColor: CHIP_BG,
  },
  number: {
    top: 6,
    left: 6,
    minWidth: 20,
    alignItems: 'center',
  },
  coins: {
    top: 6,
    right: 6,
  },
  chipText: {
    color: 'white',
    lineHeight: 15,
  },
  foot: {
    paddingHorizontal: 7,
    paddingVertical: 5,
    backgroundColor: FOOT_BG,
  },
  statePill: {
    paddingHorizontal: 7,
    paddingVertical: 2,
    backgroundColor: CHIP_BG,
  },
})
